import React, { useState, useEffect } from "react";
import { Box, Typography, AppBar, Toolbar } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MRDataProvider, { useMRData } from "./MRComponents/MRDataProvider";
import MRKanban from "./MRComponents/MRKanban";
import MRDetailsView from "./MRComponents/MRDetailsView";
import MRUpdateView from "./MRComponents/MRUpdateView";
import MRCreateModal from "./MRComponents/MRCreateModal";
import Loader from "../common/Loader";
import PluseiconDesign from "../common/PluseiconDesign";

function ManagementReviewContent() {
  const { mrData, loading, error, setRefreshTrigger } = useMRData();

  const [selectedMR, setSelectedMR] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("details");
  const [viewType, setViewType] = useState("kanban");

  // Fetch data on initial load
  useEffect(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  // Debug: Log whenever mrData changes
  useEffect(() => {
    console.log("mrData updated in ManagementReview:", mrData);
  }, [mrData]);

  // Debug log for Kanban data (MUST be before any return)
  useEffect(() => {
    console.log(
      "Passing to Kanban:",
      Array.isArray(mrData) ? mrData.map((r) => r.ID || r.id) : mrData
    );
  }, [mrData]);

  // Handle MR selection
  const handleMRClick = (mr) => {
    setSelectedMR(mr);
    setIsEditing(false);
  };

  // Handle closing the details view
  const handleCloseDetails = () => {
    setSelectedMR(null);
    setIsEditing(false);
  };

  // Handle edit button click
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Handle update success
  const handleUpdateSuccess = () => {
    setIsEditing(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  // Handle creation success
  const handleCreateSuccess = () => {
    setCreateModalOpen(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  // Handle closing the create modal
  const handleCloseCreateModal = (shouldRefresh = false) => {
    setCreateModalOpen(false);
    if (shouldRefresh) {
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const toggleViewType = () => {
    setViewType(viewType === "kanban" ? "list" : "kanban");
  };

  // Show loading indicator when loading is true (regardless of data length)
  if (loading) {
    return <Loader message="Loading Management Reviews..." />;
  }

  // Show error if there is one
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Debug log for the render
  console.log("Rendering with mrData:", mrData ? mrData.length : "no data");

  return (
    <Box sx={{ height: "calc(100vh - 64px)", overflow: "hidden" }}>
      {/* Header with title and create button */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "transparent",
          boxShadow: "none",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {/* Hide title and create button when a record is opened */}
          {!selectedMR && (
            <>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#4F46E5" }}
              >
                Management Review Kanban {mrData && `(${mrData.length})`}
              </Typography>
              <PluseiconDesign
                icon={<AddIcon />}
                variant="contained"
                onClick={handleOpenCreateModal}
              />
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Main content area */}
      <Box sx={{ display: "flex", height: "calc(100vh - 128px)", p: 2 }}>
        {/* Left side - Kanban Board (hidden when viewing details) */}
        {!selectedMR && viewType === "kanban" && (
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <MRKanban
              mrData={Array.isArray(mrData) ? mrData : []}
              onMRClick={handleMRClick}
            />
          </Box>
        )}

        {/* Right side - Details View or Edit View */}
        {selectedMR && (
          <Box sx={{ flex: 1, overflow: "auto", ml: !selectedMR ? 0 : 2 }}>
            {isEditing ? (
              <MRUpdateView
                mr={selectedMR}
                onClose={handleCloseDetails}
                onSave={handleUpdateSuccess}
              />
            ) : (
              <MRDetailsView
                mr={selectedMR}
                onClose={handleCloseDetails}
                onEdit={handleEdit}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Create Modal */}
      <MRCreateModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onCreateSuccess={handleCreateSuccess}
      />
    </Box>
  );
}

// Wrap the content with the MRDataProvider
export default function ManagementReview() {
  return (
    <MRDataProvider>
      <ManagementReviewContent />
    </MRDataProvider>
  );
}
